import * as React from 'react';

import {Divider} from '../../layout/divider';
import {StackLegacy} from '../../layout/stack/stack-legacy';

import type {ContentView} from '../visitor-sidebar';

import {CompanyDetails} from './company-details/company-details';
import type {CompanyInfo} from './company-details/company-details';
import type {Lead} from './company-details/company-lead';
import {CompanyHeader} from './company-header/company-header';
import type {CompanyHeaderDetails} from './company-header/company-header';
import {CompanyPeople} from './company-people/company-people';
import type {Person} from './company-people/company-people';
import {FooterAddButtons} from './footer-add-buttons/footer-add-buttons';
import {SessionDetails} from './session-details/session-details';
import type {Session} from './session-details/session-details';

import styles from './visitor-content.module.css';

export type Company = {
    details: CompanyHeaderDetails & CompanyInfo;
    lead?: Lead | null | undefined;
    session: Session;
};

type Props = {
    company: Company;
    people: Person[] | null | undefined;
    setContentView: (contentView: ContentView) => void;
    onClose: () => void;
    onAddPeople: (peopleIds: string[]) => Promise<any>;
    onAddCompany: (peopleIds: string[], buttonRef: any, onConfirmAdd: () => void) => any;
    unlocksRemaining: number | null | undefined;
};

export function VisitorContent(props: Props) {
    const {company} = props;

    const [selectedPersonIds, setSelectedPersonIds] = React.useState<string[]>([]);

    const companyContainerRef = React.useRef(null);
    const footerContainerRef = React.useRef(null);

    // Only companies that are already added in Nutshell have a htmlUrlPath
    const isCompanyAdded = Boolean(company.details.htmlUrlPath);

    return (
        <div className={styles['visitor-content']}>
            <div ref={companyContainerRef}>
                <div className={styles['visitor-details-container']}>
                    <StackLegacy spacing={24}>
                        <CompanyHeader
                            name={company.details.name}
                            timestamp={company.details.timestamp}
                            htmlUrlPath={company.details.htmlUrlPath}
                            wasConvertedAutomatically={company.details.wasConvertedAutomatically}
                        />
                        <CompanyDetails
                            lead={company.lead}
                            url={company.details.url}
                            location={company.details.location}
                            industry={company.details.industry}
                            revenue={company.details.revenue}
                            numEmployees={company.details.numEmployees}
                            onClose={props.onClose}
                        />
                    </StackLegacy>
                </div>
                <Divider variant='line--horizontal' />
                <SessionDetails
                    {...company.session}
                    onExpandDetails={() => props.setContentView('SESSIONS')}
                />
                <Divider variant='line--horizontal' />
            </div>
            <CompanyPeople
                people={props.people}
                selectedPersonIds={selectedPersonIds}
                updateSelectedPersonIds={(personId) =>
                    setSelectedPersonIds((prevSelectedPersonIds) =>
                        prevSelectedPersonIds.includes(personId)
                            ? prevSelectedPersonIds.filter((id) => id !== personId)
                            : [...prevSelectedPersonIds, personId]
                    )
                }
                offsetHeightRefs={[companyContainerRef, footerContainerRef]}
                onExpandPersonDetails={(personId) => props.setContentView({personId})}
                onClose={props.onClose}
            />
            <div ref={footerContainerRef}>
                {props.people ? (
                    <FooterAddButtons
                        companyName={company.details.name}
                        // @ts-expect-error
                        onAddCompany={
                            isCompanyAdded
                                ? undefined
                                : (buttonRef) =>
                                      props.onAddCompany(selectedPersonIds, buttonRef, () =>
                                          setSelectedPersonIds([])
                                      )
                        }
                        // @ts-expect-error
                        onAddPeople={
                            isCompanyAdded
                                ? () =>
                                      props
                                          .onAddPeople(selectedPersonIds)
                                          .then(() => setSelectedPersonIds([]))
                                : undefined
                        }
                        numSelectedPeople={selectedPersonIds.length}
                        unlocksRemaining={props.unlocksRemaining}
                    />
                ) : undefined}
            </div>
        </div>
    );
}
